import { Button as BorderButton } from '@aboutyou/legacy-shared/src/pages/OrderDetail/components/atoms/Button'
import { fontSize12 } from '@aboutyou/legacy-shared/src/ui/constants/typography'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Icon, { IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import { complementaryColor } from 'uiComponents/constants/colors'
import { fsXXS, fwBold } from 'uiComponents/constants/typography'
import ProductImage from '../ProductImage/ProductImage'
import ProductTileContent, {
  ProductTileContentProps,
} from './ProductTileContent'
import ProductTileWishlistButton from './ProductTileWishlistButton'

export const ProductTileSoldOutSelector = styled.section<{ href?: string }>`
  cursor: pointer;
  display: block;
  overflow: hidden;
  position: relative;
  background: ${complementaryColor};
  padding: 8px 8px 12px;
  border-radius: 4px;
  height: fit-content;
  transition: border-color 250ms ease-in-out;

  ${ProductTileWishlistButton} {
    transition: opacity 300ms ease-in-out;
  }

  :hover {
    ${ProductTileWishlistButton} {
      opacity: 1;
    }
  }
`

const InnerWrapper = styled.div`
  display: block;
  overflow: hidden;
  position: relative;
`

const Text = styled.p`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.5);
  font-size: ${fsXXS}px;
  ${fwBold};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 25%;
`

interface Props extends ProductTileContentProps {
  href?: string
  primaryImageDefaultSource: string
  primaryImageTypeTitle: string
  primaryImageSrcSet: string
  primaryPadded: boolean
  sizes?: string
  wishlisted?: boolean
  tileRef?: React.RefObject<HTMLAnchorElement | HTMLElement>
  onClick?: (ev: React.MouseEvent) => void
  onWishlistClick?: (ev: React.MouseEvent) => Promise<void> | void
  importance?: 'low' | 'high'
  alt?: string
  disableContent?: boolean
  enableAlternativesButton?: boolean
}

const ProductTileSoldOut: React.FC<Props> = ({
  wishlisted,
  onWishlistClick,
  href,
  onClick,
  tileRef,
  disableContent,
  name,
  productName,
  enableProductName,
  primaryImageDefaultSource,
  primaryImageSrcSet,
  primaryPadded,
  alt,
  sizes,
  enableAlternativesButton,
}) => {
  const [t] = useTranslation()
  const history = useHistory()
  const wishlistClickHandler = React.useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation()
      if (onWishlistClick) {
        onWishlistClick(ev)
      }
    },
    [onWishlistClick],
  )

  const onAlternativesClick = (ev: React.MouseEvent) => {
    ev.stopPropagation()
    ev.preventDefault()

    history.push(href + '/similar')
  }

  return (
    <ProductTileSoldOutSelector
      data-testid="ProductTile"
      as={href ? 'a' : 'section'}
      href={href}
      onClick={onClick}
      ref={tileRef}
    >
      <InnerWrapper>
        <ProductImage
          padded={primaryPadded}
          src={primaryImageDefaultSource}
          srcSet={primaryImageSrcSet}
          sizes={sizes}
          alt={alt}
        />
        <Text data-testid="SoldOutProductText">{t('Leider ausverkauft')}</Text>
        {wishlisted && (
          <ProductTileWishlistButton
            onClick={wishlistClickHandler}
            filled={wishlisted}
          />
        )}
        {enableAlternativesButton && (
          <StyledButton onClick={onAlternativesClick}>
            <Icon asset={IconName.IcStore} size={IconSize.XSS} />
            <span>{t('Alternativen')}</span>
          </StyledButton>
        )}
      </InnerWrapper>
      {!disableContent && (
        <ProductTileContent
          name={name}
          productName={productName}
          formattedBasePrice={undefined}
          enableProductName={enableProductName}
          href={href}
          enableAlternativesButton={enableAlternativesButton}
        />
      )}
    </ProductTileSoldOutSelector>
  )
}

const StyledButton = styled(BorderButton)`
  width: calc(100% - 32px);
  display: flex;
  gap: 6px;
  align-items: center;
  ${fontSize12};
  font-weight: 700;

  position: absolute;
  left: 16px;
  bottom: 15px;
`

export default ProductTileSoldOut
